import { SignUpAttribute } from "@aws-amplify/ui";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { DefaultComponents } from "@aws-amplify/ui-react/dist/types/components/Authenticator/hooks/useCustomComponents/defaultComponents";
import { useEffect } from "react";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { signInComponent } from "./components/SignInComponents";
import { signUpComponent } from "./components/SignUpComponents";
import { confirmSignUpComponent } from "./components/ConfirmSignUpComponents";
import { resetPasswordComponent, confirmResetPasswordComponent } from "./components/ResetPasswordComponents";
import { forceNewPasswordComponent } from "./components/ForceNewPassword";
import { authFormFields } from "./formFields";
import { authServices } from "./services";

const signUpAttributes: SignUpAttribute[] = ["given_name", "family_name", "email"];

const components: DefaultComponents = {
  SignIn: signInComponent,
  SignUp: signUpComponent,
  ConfirmSignUp: confirmSignUpComponent,
  ResetPassword: resetPasswordComponent,
  ConfirmResetPassword: confirmResetPasswordComponent,
  ForceNewPassword: forceNewPasswordComponent,
};

interface AuthenticatorViewProps {
  initialState: "signIn" | "signUp" | "resetPassword";
}

export function AuthenticatorView({ initialState }: AuthenticatorViewProps) {
  const { route, toSignIn, toSignUp, toResetPassword } = useAuthenticator((context) => [context.route]);
  const navigate = useNavigateRoute();

  useEffect(() => {
    switch (initialState) {
      case "signIn":
        toSignIn();
        break;
      case "signUp":
        toSignUp();
        break;
      case "resetPassword":
        toResetPassword();
        break;
    }
  }, [initialState, toSignIn, toSignUp, toResetPassword]);

  useEffect(() => {
    const routeToPathMap: Record<string, string> = {
      signIn: "/auth/sign-in",
      signUp: "/auth/sign-up",
      resetPassword: "/auth/reset-password",
      confirmResetPassword: "/auth/reset-password", // Stay on reset password during confirmation
      confirmSignUp: "/auth/sign-up", // Stay on sign-up during confirmation
    };

    const targetPath = routeToPathMap[route];

    if (targetPath && window.location.pathname !== targetPath) {
      navigate(targetPath, { replace: false });
    }
  }, [route, navigate]);

  return (
    <Authenticator
      initialState={initialState}
      components={components}
      formFields={authFormFields}
      services={authServices}
      signUpAttributes={signUpAttributes}
    />
  );
}
